import { TKV2_ORDERS_API, noop, schSubscriptionResponse } from '@aloft/toolkit-shared';
import { useAuth0 } from '@auth0/auth0-react';

import { useEffect, useMemo, useRef } from 'react';
import { Toaster } from 'react-hot-toast';

import { AuthWrapper } from '../AuthWrapper.tsx';
import { Analytics } from '../components/Analytics/Analytics.tsx';
import { OutOfDateNotifier } from '../components/OutOfDateNotifier/OutOfDateNotifier.tsx';
import { setGlobalUserID } from '../featureDecisions/index.ts';
import { useGetAccount } from '../hooks/api/schema-hooks/useGetAccount.ts';
import { useGetTkv2OrdersAPI } from '../hooks/api/schema-hooks/useGetTkv2OrdersAPI.ts';
import { useGetStripeSubscriptionStatus } from '../hooks/api/toolkitComponents.ts';
import { useSessionID } from '../hooks/useSessionID.ts';
import { setLogIdentifier } from '../logging/logging.ts';
import { loadAccount } from '../redux/features/accountSlice.ts';
import { selectUser } from '../redux/features/selectors/accountSelectors.ts';
import { useAppDispatch, useAppSelector } from '../redux/hooks.ts';
import { whatEnv } from '../utils/whatEnv.ts';
import { Auth0ProviderWithHistory } from './Auth0ProviderWithHistory.tsx';
import { GlobalLoadingContextProvider } from './GlobalLoadingContextProvider.tsx';
import { StripeCustomerIDWrapper } from './StripeCustomerIDWrapper.tsx';
import { ToolkitControllerGlobalContext } from './consts.ts';
import { getMlsListingsBatcher } from './hooks/listings/index.ts';
import { RootRouteElement } from './routes/RootRouteElement.tsx';
import { ToolkitControllerGlobal } from './types.ts';

const ToolkitControllerInner = ({ global }: { global: ToolkitControllerGlobal }) => {
  const { user: auth0User, getIdTokenClaims } = useAuth0();
  const sessionID = useSessionID();
  const user = useAppSelector(selectUser);

  useEffect(() => {
    if (auth0User) {
      const getClaims = async () => {
        const token = await getIdTokenClaims();

        setLogIdentifier({
          sessionID,
          email: user.email,
          name: `${user.firstName || ''} ${user.lastName || ''}`,
          uid: user.id,
          isTestUser: (token?.['app_metadata.is_test_user'] as boolean) ?? false,
          env: whatEnv(),
        });
      };
      getClaims().then(noop).catch(noop);
    }
  }, [getIdTokenClaims, auth0User, sessionID, user]);

  return (
    <ToolkitControllerGlobalContext.Provider value={global}>
      <Analytics />
      <RootRouteElement />
      <OutOfDateNotifier />
      <Toaster />
    </ToolkitControllerGlobalContext.Provider>
  );
};

const useNewToolkitControllerGlobal = ():
  | ToolkitControllerGlobal
  | undefined
  | { isNewToolkitControllerError: true; error: string } => {
  const dispatch = useAppDispatch();
  const accountLoaded = useRef(false); // using this so we only load into redux once.

  const accountReq = useGetAccount({});

  // FIXME: Seems like this is not triggering until rerender in beta. Something weird going on with feature flags
  const tkv2OrdersReq = useGetTkv2OrdersAPI({});

  const mlsListingsBatcher = useMemo(() => getMlsListingsBatcher(), []);

  const subscriptionReq = useGetStripeSubscriptionStatus({});

  if (accountReq.isError) {
    return { isNewToolkitControllerError: true, error: 'Could not locate account' };
  }

  if (tkv2OrdersReq.isError) {
    return { isNewToolkitControllerError: true, error: 'Failed to load orders' };
  }

  if (subscriptionReq.isError) {
    return { isNewToolkitControllerError: true, error: 'Failed to load subscription status' };
  }

  if (accountReq.isLoading || tkv2OrdersReq.isLoading || subscriptionReq.isLoading) {
    return undefined;
  }

  setGlobalUserID(accountReq.data!.id);

  if (!accountLoaded.current) {
    accountLoaded.current = true;
    dispatch(loadAccount(accountReq.data!));
  }

  const tkv2Orders = tkv2OrdersReq.data && TKV2_ORDERS_API.GET.RESPONSE.parse(tkv2OrdersReq.data);
  const subscription = schSubscriptionResponse.parse(subscriptionReq.data);

  return {
    tkv2Orders: tkv2Orders?.results ?? [],
    isNewToolkitControllerError: false,
    mlsListingsBatcher,
    subscription,
  };
};

const ToolkitControllerGlobalWrapper = () => {
  const global = useNewToolkitControllerGlobal();

  if (!global) return null;

  // Let global error boundary handle
  if (global.isNewToolkitControllerError) throw new Error(global.error);

  return <ToolkitControllerInner global={global} />;
};

export const ToolkitController = () => (
  <Auth0ProviderWithHistory>
    <GlobalLoadingContextProvider>
      <AuthWrapper>
        <StripeCustomerIDWrapper>
          <ToolkitControllerGlobalWrapper />
        </StripeCustomerIDWrapper>
      </AuthWrapper>
    </GlobalLoadingContextProvider>
  </Auth0ProviderWithHistory>
);
